import FuseUtils from '@fuse/utils/FuseUtils';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import apiEndpoints from 'app/constants/apiEndpoints';
import { isInstalledApp } from 'app/main/utils/installedApp';

const {
  AUTH: AUTH_ENDPOINT,
  GOOGLE: GOOGLE_ENDPOINT,
  USERS: USERS_ENDPOINT,
  USER_DATA,
  ENCOMPASS_LOGIN: ENCOMPASS_LOGIN_ENDPOINT
} = apiEndpoints;

const TOKEN_KEY = 'jwt_access_token';
const LOGOUT_BROADCAST_KEY = 'jwt_logout_broadcast';

// An installed home-screen app gets killed by the OS and would lose a sessionStorage token on every reopen.
const tokenStorage = () => (isInstalledApp() ? window.localStorage : window.sessionStorage);

/* eslint-disable camelcase */

class JwtService extends FuseUtils.EventEmitter {
  init() {
    this.setInterceptors();
    this.listenForLogoutBroadcast();
    this.handleAuthentication();
  }

  setInterceptors = () => {
    axios.interceptors.response.use(
      response => {
        return response;
      },
      err => {
        return new Promise(() => {
          // Guarded: without a response (timeout, CORS, dropped connection)
          // reading `err.response.status` threw a TypeError that replaced the
          // real error, leaving callers with an empty payload and no clue why.
          const status = err.response && err.response.status;
          const serverMessage = err.response && err.response.data && err.response.data.error
            ? err.response.data.error.message
            : 'Your session is no longer valid. Please sign in again.';

          if (!err.response) {
            this.emit('onServerError', 'A Server Error occurred! Please check your Internet Connection.');
          } else if (status === 401 && err.config && !err.config.__isRetryRequest) {
            // The session itself is over — expired, revoked, or the account's access
            // level changed underneath it. Signing the user out is the right answer.
            this.emit('onAutoLogout', serverMessage);
          }

          /*
            A 403 deliberately does **not** end the session.

            It used to, back when the API only ever answered 403 for a session it had
            stopped trusting. Now that permissions are enforced per route, 403 is the
            ordinary answer to "this account may not read that list" — and any screen
            that asks for one thing it is not allowed was signing the person out
            mid-click, resetting the store to its shipped placeholder account and
            leaving somebody else's name in the toolbar.

            The rejection still reaches the caller below, which is where a screen
            decides what a refusal means for it.
          */
          throw err;
        });
      }
    );
  };

  handleAuthentication = () => {
    const access_token = this.getAccessToken();
    if (!access_token) {
      this.emit('onNoAccessToken');
      return;
    }

    if (this.isAuthTokenValid(access_token)) {
      this.setSession(access_token);
      this.emit('onAutoLogin', true);
    } else {
      this.setSession(null);
      this.emit('onAutoLogout', 'Access expired');
    }
  };

  // createUser = data => {
  //   return new Promise((resolve, reject) => {
  //     axios.post('/api/auth/register', data).then(response => {
  //       if (response.data.user) {
  //         this.setSession(response.data.access_token);
  //         resolve(response.data.user);
  //       } else {
  //         reject(response.data.error);
  //       }
  //     });
  //   });
  // };

  signInWithEmailAndPassword = (email, password) => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${USERS_ENDPOINT}login`, {
          email,
          password
        })
        .then(response => {
          const { data } = response;
          if (data.data && data.data.user) {
            this.setSession(data.data.token);
            resolve(data.data.user);
          } else {
            reject(data.error);
          }
        });
    });
  };

  signInWithGoogle = idToken => {
    return new Promise((resolve, reject) => {
      axios
        .post(`${GOOGLE_ENDPOINT}login`, {
          idToken
        })
        .then(response => {
          const { data } = response;
          if (data.data && data.data.user) {
            this.setSession(data.data.token);
            resolve(data.data.user);
          } else {
            reject(data.error);
          }
        });
    });
  };

  forgotPassword = email => {
    return axios.post(`${AUTH_ENDPOINT}forgot-password`, {
      email
    });
  };

  resetPassword = (token, password, passwordConfirmation) => {
    return axios.post(`${AUTH_ENDPOINT}reset-password`, {
      token,
      password,
      passwordConfirmation
    });
  };

  acceptInvitation = (token, password, passwordConfirmation) => {
    return axios.post(`${AUTH_ENDPOINT}accept-invitation`, {
      token,
      password,
      passwordConfirmation
    });
  };

  sendInvitation = userId => {
    return axios.post(`${USERS_ENDPOINT}${userId}/invite`);
  };


  signInWithEncompassToken = (authorizationCode, redirectUri) => {
    // Sent explicitly: on the way back from Encompass `Auth` skips `init`, so the axios default is unset.
    const currentToken = this.getAccessToken();
    return new Promise((resolve, reject) => {
      axios
        .post(
          `${ENCOMPASS_LOGIN_ENDPOINT}login`,
          { authorizationCode, redirectUri },
          { headers: { Authorization: `Bearer ${currentToken}` } }
        )
        .then(response => {
          const { data } = response;
          if (data.data && data.data.user) {
            this.setSession(data.data.token);
            resolve(data.data.user);
          } else {
            reject(data.error);
          }
        })
        .catch(error => reject(error.response?.data?.error || error));
    });
  };

  signInWithToken = () => {
    return new Promise((resolve, reject) => {
      const token = this.getAccessToken();
      axios
        .post(`${USERS_ENDPOINT}access-token`, 
          {token}
        )
        .then(response => {
          const user = response.data?.data?.user;
        if (user) {
          this.setSession(token); 
          resolve(user);
          } else {
            this.logout();
            reject(new Error('Failed to login with token.'));
          }
        })
        .catch(() => {
          this.logout();
          reject(new Error('Failed to login with token.'));
        });
    });
  };

  updateUserData = user => {
    return axios.post(USER_DATA, {
      user
    });
  };

  setSession = access_token => {
    if (access_token) {
      tokenStorage().setItem(TOKEN_KEY, access_token);
      axios.defaults.headers.common.Authorization = `Bearer ${access_token}`;
    } else {
      tokenStorage().removeItem(TOKEN_KEY);
      delete axios.defaults.headers.common.Authorization;
    }
  };

  logout = (message = null) => {
    this.setSession(null);
    this.broadcastLogout();
    if(message) this.emit('onUnauthorizedUser', message);
  };

  // The token is per-tab now, so the other tabs only hear about a logout through localStorage.
  broadcastLogout = () => {
    localStorage.setItem(LOGOUT_BROADCAST_KEY, String(Date.now()));
    localStorage.removeItem(LOGOUT_BROADCAST_KEY);
  };

  listenForLogoutBroadcast = () => {
    window.addEventListener('storage', event => {
      if (event.key === LOGOUT_BROADCAST_KEY && event.newValue && this.getAccessToken()) {
        this.setSession(null);
        window.location.reload();
      }
    });
  };

  isAuthTokenValid = access_token => {
    if (!access_token) {
      return false;
    }
    const decoded = jwtDecode(access_token);
    const currentTime = Date.now() / 1000;
    if (decoded.exp < currentTime) {
      console.warn('access token expired');
      return false;
    }

    return true;
  };

  getAccessToken = () => {
    return tokenStorage().getItem(TOKEN_KEY);
  };
}

const instance = new JwtService();

export default instance;
